/*-----------------------------------------------------------------------------/
/ Custom Theme JS
/-----------------------------------------------------------------------------*/

// Insert any custom theme js here...
class HeaderPredictiveSearch extends HTMLElement {
 constructor() {
   super();

   this.form = this.querySelector('form');
   this.input = this.querySelector('input[type="search"]');
   this.predictiveSearchResults = this.querySelector('.header-predictive-search__results');
   this.boundOnDocumentClick = this.onDocumentClick.bind(this);
   this.boundOnKeydown = this.onKeydown.bind(this);
   this.boundOnResultsClick = this.onResultsClick.bind(this);

   this.input.addEventListener('input', this.debounce((event) => {
     this.onChange(event);
   }, 300).bind(this));
 }

 connectedCallback() {
   document.addEventListener('click', this.boundOnDocumentClick);
   document.addEventListener('keydown', this.boundOnKeydown);
   this.predictiveSearchResults.addEventListener('click', this.boundOnResultsClick);
 }

 disconnectedCallback() {
   document.removeEventListener('click', this.boundOnDocumentClick);
   document.removeEventListener('keydown', this.boundOnKeydown);
   this.predictiveSearchResults.removeEventListener('click', this.boundOnResultsClick);
 }

 onChange() {
   const searchTerm = this.input.value.trim();

   if (!searchTerm.length) {
     this.close();
     return;
   }

   this.getSearchResults(searchTerm);
 }

 getSearchResults(searchTerm) {
   const query = encodeURIComponent(searchTerm);

   fetch(`/search/suggest?q=${query}&section_id=predictive-search`)
     .then((response) => {
       if (!response.ok) {
         var error = new Error(response.status);
         this.close();
         throw error;
       }

       return response.text();
     })
     .then((text) => {
       const suggestDoc = new DOMParser().parseFromString(text, 'text/html');
       const resultsMarkup = suggestDoc.querySelector('#shopify-section-predictive-search').innerHTML;

      
       if (!suggestDoc.querySelector('#predictive-search-results-list')) {
         return this.getSkuSearchResults(searchTerm, resultsMarkup);
       }

       return resultsMarkup;
     })
     .then((markup) => {
       this.predictiveSearchResults.innerHTML = markup;
       if (this.predictiveSearchResults.textContent.trim().length) {
         this.open();
       } else {
         this.close();
       }
     })
     .catch((error) => {
       this.close();
       throw error;
     });
 }

 
 getSkuSearchResults(searchTerm, fallbackMarkup) {
   const url = `/search?q=${encodeURIComponent(searchTerm)}&type=product&section_id=main-search`;

   return fetch(url)
     .then((response) => (response.ok ? response.text() : null))
     .then((text) => {
       if (!text) return fallbackMarkup;

       const doc = new DOMParser().parseFromString(text, 'text/html');
       const cards = Array.from(doc.querySelectorAll('.product-card'));
       const term = searchTerm.toLowerCase();

       // The full search matches titles, tags and descriptions too, so a SKU
       // query brings back unrelated products. Keep only cards whose SKU
       // matches the term.
       const matches = cards.filter((card) => {
         const sku = card.querySelector('product-card-sku');
         if (!sku) return false;
         return sku.textContent.trim().toLowerCase().startsWith(term);
       });

       if (!matches.length) return fallbackMarkup;

       return this.buildSkuResultsMarkup(matches, searchTerm);
     })
     .catch(() => fallbackMarkup);
 }

 buildSkuResultsMarkup(cards, searchTerm) {
   const items = cards
     .map((card, index) => {
       const link = card.querySelector('a[href]');
       if (!link) return '';

       const href = link.getAttribute('href').split('?')[0];
       const title = (card.querySelector('product-card-title') || {}).textContent || link.title || '';
       const sku = (card.querySelector('product-card-sku') || {}).textContent || '';
       const price = (card.querySelector('product-card-price') || {}).innerHTML || '';
       const image = card.querySelector('.product-card__img, img');
       const imageMarkup = image
         ? `<img class="predictive-search__image o-ratio__content" src="${image.getAttribute('src') || ''}" srcset="${image.getAttribute('srcset') || ''}" sizes="5rem" alt="${image.getAttribute('alt') || ''}" loading="lazy">`
         : '';

       return `
         <li id="predictive-search-product-${index + 1}" class="predictive-search__list-item" role="option">
           <a href="${href}" class="predictive-search__item" tabindex="-1">
             <div class="o-ratio o-ratio--bg-product">${imageMarkup}</div>
             <div class="predictive-search__item-content">
               <span class="predictive-search__item-heading">${title.trim()}</span>
               <span class="predictive-search__item-heading">${sku.trim()}</span>
               <span class="predictive-search__item-price">${price}</span>
             </div>
           </a>
         </li>
       `;
     })
     .join('');

   const safeTerm = searchTerm.replace(/[&<>"]/g, (char) => `&#${char.charCodeAt(0)};`);
   const labelTemplate = this.getAttribute('data-search-for-label') || 'Search for “__terms__”';
   const searchForLabel = labelTemplate.replace('__terms__', safeTerm);

   return `
     <div id="predictive-search-results" class="predictive-search has-results">
       <div class="predictive-search__panels predictive-search__panels--has-products has-products-${cards.length}">
         <div class="predictive-search__panel predictive-search__panel--products">
           <ul id="predictive-search-results-list" class="predictive-search__results-list o-list-bare" role="listbox">
             ${items}
           </ul>
         </div>
       </div>
       <div class="predictive-search__triggers">
         <button id="predictive-search-trigger" class="c-btn c-btn--primary c-btn--full predictive-search__btn" tabindex="-1">
           ${searchForLabel}
         </button>
       </div>
     </div>
   `;
 }

 open() {
   this.predictiveSearchResults.style.display = 'block';
   this.input.setAttribute('aria-expanded', 'true');
 }

 close() {
   this.predictiveSearchResults.style.display = 'none';
   this.input.setAttribute('aria-expanded', 'false');
 }

 onDocumentClick(event) {
   if (!this.contains(event.target)) {
     this.close();
   }
 }

 onKeydown(event) {
   if (event.key === 'Escape') {
     this.close();
   }
 }

 onResultsClick(event) {
   const triggerButton = event.target.closest('#predictive-search-trigger');
   if (!triggerButton) return;

   event.preventDefault();

   if (this.form) {
     this.form.requestSubmit();
   }
 }

 debounce(fn, wait) {
   let t;
   return (...args) => {
     clearTimeout(t);
     t = setTimeout(() => fn.apply(this, args), wait);
   };
 }
}

customElements.define('header-predictive-search', HeaderPredictiveSearch);


/*-----------------------------------------------------------------------------/
/ CartStore patch (assets/cart-store.js)
/-----------------------------------------------------------------------------/
/ /cart.js can occasionally answer with an HTML page instead of JSON
/ (throttling, expired preview session, challenge page). Unpatched,
/ CartStore.getCart() feeds that straight into .json(), which throws
/ "SyntaxError: Unexpected token <" and is rendered verbatim next to a
/ cart line item by cart-components.js's #renderError(). Retry with
/ backoff, then fail with a message a shopper can read. Also guard
/ subscribe(): cart-store notifies subscribers synchronously during
/ add/change/update, so one throwing subscriber otherwise aborts the
/ whole cart action for every other subscriber.
/
/ This module is loaded (type="module", right after cart-store.js and
/ before cart-link.js/cart-components.js) so the patch is applied
/ synchronously before any consumer calls CartStore.getCart() or
/ Cart.subscribe() — see layout/theme.liquid.
/-----------------------------------------------------------------------------*/
import { CartStore } from "cart-store";

const originalGetCart = CartStore.getCart;
const GETCART_RETRY_DELAYS_MS = [ 400, 800 ];

CartStore.getCart = async function patchedGetCart() {
  let lastError;
  for (let attempt = 0; attempt <= GETCART_RETRY_DELAYS_MS.length; attempt++) {
    try {
      return await originalGetCart();
    } catch (e) {
      lastError = e;
      if (attempt < GETCART_RETRY_DELAYS_MS.length) {
        await new Promise((resolve) => setTimeout(resolve, GETCART_RETRY_DELAYS_MS[attempt]));
      }
    }
  }
  console.error("[cart-store patch] getCart failed after retries:", lastError);
  throw new Error("We couldn't load your cart. Please refresh the page and try again.");
};

const originalSubscribe = CartStore.prototype.subscribe;

CartStore.prototype.subscribe = function patchedSubscribe(callback) {
  const safeCallback = function (...args) {
    try {
      return callback.apply(this, args);
    } catch (e) {
      console.error("[cart-store patch] cart subscriber threw, ignoring:", e);
    }
  };
  return originalSubscribe.call(this, safeCallback);
};